import { trpcClient } from "@/trpc";
import { uploadWithServerToken } from "@/ajax/upload/server-issued-upload";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { contentTypeFor } from "@/common/utils/upload-content-type";

const RECEIPT_BUCKET = "expense-receipts";

/** A receipt the API just reserved, with its single-use upload grant. */
export interface ReservedReceiptUpload {
  path: string;
  uploadToken: string;
}

/**
 * Upload a receipt file to the path the API reserved for it, using the
 * server-issued token rather than the user's JWT.
 *
 * Each reservation has a fresh path, so the token never overwrites anything;
 * a failed submission is retried by reserving again, which issues new tokens.
 */
export async function uploadReservedReceipt({
  file,
  receipt,
}: {
  file: File;
  receipt: ReservedReceiptUpload;
}): Promise<void> {
  try {
    // The bucket restricts content types; a HEIC the browser left untyped must
    // still arrive as image/heic.
    await uploadWithServerToken({
      upload: {
        bucketName: RECEIPT_BUCKET,
        storagePath: receipt.path,
        token: receipt.uploadToken,
      },
      body: file,
      contentType: contentTypeFor(file),
    });
  } catch (error) {
    console.error("Receipt upload error:", error);
    const reason = error instanceof Error ? error.message : "unknown error";
    throw new Error(`Failed to upload receipt: ${reason}`);
  }
}

/**
 * Get a signed URL for viewing a receipt. The API authorizes the caller by
 * receipt id; the URL is valid for 1 hour.
 */
export async function getReceiptUrl(receiptId: string): Promise<string> {
  const { url } = await trpcClient.expense.getReceiptUrl.query({ receiptId });
  return url;
}

/**
 * Open a receipt URL, using the native in-app browser on iOS/Android
 * and a standard browser tab on web.
 */
export async function openReceiptUrl(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url, presentationStyle: "popover" });
  } else {
    window.open(url, "_blank");
  }
}

/**
 * Validate file type for receipts
 */
export function isValidReceiptFile(file: File): boolean {
  const validTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/heic",
    "application/pdf",
  ];
  return validTypes.includes(file.type);
}

/**
 * Validate file size (max 5MB)
 */
export function isValidReceiptSize(file: File): boolean {
  const maxSize = 5 * 1024 * 1024; // 5MB
  return file.size <= maxSize;
}

/**
 * Get file type display name
 */
export function getFileTypeDisplay(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return "PDF";
    case "jpg":
    case "jpeg":
      return "JPG";
    case "png":
      return "PNG";
    case "heic":
      return "HEIC";
    default:
      return ext?.toUpperCase() || "File";
  }
}
